import { useEffect, useState } from "react";
import {
  isStagingBuildHost,
  isStagingBuildInfo,
  shortBuildId,
  type BackendBuildInfo
} from "./build-info";

type BackendState = BackendBuildInfo | "loading" | "unavailable";

export function BuildBadge() {
  const staging = isStagingBuildHost(window.location.hostname);
  const [backend, setBackend] = useState<BackendState>("loading");

  useEffect(() => {
    if (!staging) return;
    const controller = new AbortController();

    void fetch("/api/build-info", {
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal: controller.signal
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Build info request failed with ${response.status}`);
      const value: unknown = await response.json();
      if (!isStagingBuildInfo(value)) throw new Error("Invalid build info response");
      setBackend(value.backend);
    }).catch((error: unknown) => {
      if (!(error instanceof DOMException && error.name === "AbortError")) setBackend("unavailable");
    });

    return () => controller.abort();
  }, [staging]);

  if (!staging) return null;

  const frontend = __CRAFTOFF_FRONTEND_BUILD__;
  const backendLabel = typeof backend === "object" ? shortBuildId(backend.versionId) : backend;
  const backendDetails = typeof backend === "object"
    ? `${backend.versionId}${backend.tag ? ` (${backend.tag})` : ""} deployed ${backend.deployedAt}`
    : backend;
  const title = [
    `Frontend commit: ${frontend.commit}`,
    `Frontend built: ${frontend.builtAt}`,
    `Backend version: ${backendDetails}`
  ].join("\n");

  return (
    <aside className="staging-build-badge" aria-label={`Staging build: frontend ${frontend.commit}, backend ${backendLabel}`} title={title}>
      <b>STAGING</b>
      <span>FE {shortBuildId(frontend.commit)}</span>
      <i aria-hidden="true">·</i>
      <span>BE {backendLabel}</span>
    </aside>
  );
}
